/**
 * Nested Product Accordion Component (Child Level)
 *
 * @package
 */

import { useState, useId } from '@wordpress/element';
import { _n, sprintf } from '@wordpress/i18n';
import ProductCard from './ProductCard';
import { useGroupVariants } from '../helpers/useGroupVariants';
import { getActiveVariantId, isProductSelected, makeGroupSelectionHandlers } from '../helpers/variantSelection';

const NestedProductAccordion = ( { subGroup, selectedProducts, setSelectedProducts, lockedProducts = [], vatMode = 'including', quantities = {}, setProductQuantity, workshopHourlyRate = 0, taxRate = 0 } ) => {
	const startClosed = subGroup.sub_group_accordion_start_closed === true || subGroup.sub_group_accordion_start_closed === 1;
	const [ isOpen, setIsOpen ] = useState( ! startClosed );

	const selectionType = subGroup.sub_group_selection_type || 'checkbox';
	const cardLayout = subGroup.sub_group_card_layout || 'horizontal';

	const subGroupProductIds = subGroup.products || [];
	const hasLockedProductInGroup = lockedProducts.some( lockedId => subGroupProductIds.includes( lockedId ) );
	const isRadioGroupDisabled = selectionType === 'radio' && hasLockedProductInGroup;

	const { variantsMap, groupVariantIds } = useGroupVariants( subGroupProductIds );
	const { toggleProduct, selectVariant } = makeGroupSelectionHandlers( {
		selectionType,
		groupProductIds: subGroupProductIds,
		groupVariantIds,
		selectedProducts,
		setSelectedProducts,
	} );

	const accordionId = `nested-accordion${ useId() }`;
	const contentId = `${ accordionId }-content`;

	const subGroupName = subGroup.sub_group_name || 'Sub-group';

	const selectedCount = subGroupProductIds.filter(
		productId => isProductSelected( productId, variantsMap, selectedProducts )
	).length;

	return (
		<div className="configurator-nested-product-accordion">
			<button
				type="button"
				className="configurator-nested-product-accordion-header"
				onClick={ () => setIsOpen( ! isOpen ) }
				aria-expanded={ isOpen }
				aria-controls={ contentId }
				id={ accordionId }
			>
				<span className="configurator-nested-product-accordion-name">
					{ subGroupName }
					{ selectedCount > 0 && (
						<span className="configurator-product-accordion-selected-count">
							<span aria-hidden="true">{ selectedCount }</span>
							<span className="screen-reader-text">
								{ sprintf(
									/* translators: %d: number of selected options in the group. */
									_n( '%d option selected', '%d options selected', selectedCount, 'at-vehicle-configurator' ),
									selectedCount,
								) }
							</span>
						</span>
					) }
				</span>
				<span className="configurator-accordion-icon" aria-hidden="true">
					{ isOpen ? '−' : '+' }
				</span>
			</button>
			{ isOpen && (
				<div
					id={ contentId }
					className="configurator-nested-product-accordion-content"
					role="region"
					aria-labelledby={ accordionId }
				>
					{ subGroup.sub_group_description && (
						<div
							className="configurator-nested-product-accordion-description"
							dangerouslySetInnerHTML={ { __html: subGroup.sub_group_description } }
						/>
					) }
					{ subGroup.products && subGroup.products.length > 0 && (
						<div className={ `configurator-products-list ${ selectionType === 'radio' ? 'configurator-products-list-radio' : '' } ${ cardLayout === 'grid-2' ? 'configurator-products-list-grid-2' : '' } ${ cardLayout === 'grid-3' ? 'configurator-products-list-grid-3' : '' }` }>
							{ subGroup.products.map( productId => {
								const isLocked = lockedProducts.includes( productId );
								const isDisabled = isRadioGroupDisabled && ! isLocked;
								const variants = variantsMap[ productId ] || [];
								const variantIds = variants.map( v => v.id );
								const activeVariantId = getActiveVariantId( variants, selectedProducts );
								const isSelected = variants.length > 0
									? activeVariantId !== null
									: selectedProducts.includes( productId );
								// The configuration stores the variant's ID, so its quantity keys off that.
								const quantityId = variants.length > 0 ? activeVariantId : productId;
								return (
									<ProductCard
										key={ productId }
										productId={ productId }
										isSelected={ isSelected }
										onToggle={ () => toggleProduct( productId ) }
										selectionType={ selectionType }
										isLocked={ isLocked }
										isDisabled={ isDisabled }
										layout={ cardLayout }
										vatMode={ vatMode }
										variants={ variants }
										activeVariantId={ activeVariantId }
										onSelectVariant={ id => selectVariant( id, variantIds ) }
										quantity={ quantities[ quantityId ] || 1 }
										onQuantityChange={ units => setProductQuantity( quantityId, units ) }
										workshopHourlyRate={ workshopHourlyRate }
										taxRate={ taxRate }
									/>
								);
							} ) }
						</div>
					) }
				</div>
			) }
		</div>
	);
};

export default NestedProductAccordion;
